import { DestroyRef, Injectable, computed, inject, isDevMode, signal } from '@angular/core';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

type MobilePlatform = 'android' | 'ios' | 'other';

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

@Injectable({ providedIn: 'root' })
export class PwaInstallPromptService {
  private static readonly dismissStorageKey = 'hubnari-pwa-install-dismissed-at';
  private static readonly dismissCooldownMs = 7 * 24 * 60 * 60 * 1000;

  private readonly destroyRef = inject(DestroyRef);
  private readonly deferredPrompt = signal<BeforeInstallPromptEvent | null>(null);
  private readonly dismissedRecently = signal(false);
  private readonly mobilePlatform = signal<MobilePlatform>('other');
  private readonly standaloneMode = signal(false);

  readonly canInstall = computed(() => this.deferredPrompt() !== null);
  readonly visible = computed(
    () =>
      !isDevMode() &&
      this.mobilePlatform() !== 'other' &&
      !this.standaloneMode() &&
      !this.dismissedRecently(),
  );
  readonly title = computed(() =>
    this.canInstall() ? 'Instale o Hubnari no seu celular' : 'Adicione o Hubnari a tela inicial',
  );
  readonly description = computed(() => {
    if (this.canInstall()) {
      return 'Toque em Instalar para abrir o Hubnari como app, com acesso rapido e tela cheia.';
    }

    if (this.mobilePlatform() === 'ios') {
      return 'No Safari, toque em Compartilhar e depois em Adicionar a Tela de Inicio para instalar o app.';
    }

    return 'Abra o menu do navegador e use Instalar app ou Adicionar a tela inicial para instalar o Hubnari.';
  });

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    this.mobilePlatform.set(this.detectMobilePlatform());
    this.standaloneMode.set(this.detectStandaloneMode());
    this.dismissedRecently.set(this.wasDismissedRecently());

    if (this.mobilePlatform() === 'other' || this.standaloneMode()) {
      return;
    }

    const handleBeforeInstallPrompt = (event: Event): void => {
      const installEvent = event as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      this.deferredPrompt.set(installEvent);
    };

    const handleAppInstalled = (): void => {
      this.deferredPrompt.set(null);
      this.standaloneMode.set(true);
      window.localStorage.removeItem(PwaInstallPromptService.dismissStorageKey);
      this.dismissedRecently.set(false);
    };

    const displayModeMediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (): void => {
      this.standaloneMode.set(this.detectStandaloneMode());
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    displayModeMediaQuery.addEventListener('change', handleDisplayModeChange);

    this.destroyRef.onDestroy(() => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      displayModeMediaQuery.removeEventListener('change', handleDisplayModeChange);
    });
  }

  dismiss(): void {
    const dismissedAt = Date.now().toString();
    window.localStorage.setItem(PwaInstallPromptService.dismissStorageKey, dismissedAt);
    this.dismissedRecently.set(true);
  }

  async install(): Promise<void> {
    const promptEvent = this.deferredPrompt();

    if (!promptEvent) {
      return;
    }

    await promptEvent.prompt();

    const choiceResult = await promptEvent.userChoice;
    this.deferredPrompt.set(null);

    if (choiceResult.outcome === 'accepted') {
      this.standaloneMode.set(true);
      return;
    }

    this.dismissedRecently.set(this.wasDismissedRecently());
  }

  private detectMobilePlatform(): MobilePlatform {
    const userAgent = window.navigator.userAgent.toLowerCase();

    if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'ios';
    }

    if (/android/.test(userAgent)) {
      return 'android';
    }

    return 'other';
  }

  private detectStandaloneMode(): boolean {
    const navigatorWithStandalone = window.navigator as NavigatorWithStandalone;

    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      navigatorWithStandalone.standalone === true
    );
  }

  private wasDismissedRecently(): boolean {
    const rawValue = window.localStorage.getItem(PwaInstallPromptService.dismissStorageKey);

    if (!rawValue) {
      return false;
    }

    const dismissedAt = Number(rawValue);

    if (!Number.isFinite(dismissedAt)) {
      window.localStorage.removeItem(PwaInstallPromptService.dismissStorageKey);
      return false;
    }

    return Date.now() - dismissedAt < PwaInstallPromptService.dismissCooldownMs;
  }
}
